import { inject, Injectable } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { GlobalErrorService } from '@services/global-error.service'
import { CertificateHolderService } from '@features/certificate-holder/services/certificate-holder.service'
import { CertificateHolderEmail, CertificateHolderForm, CertificateHolderUsedIn } from '@features/certificate-holder/models/certificate-holder.model'
import { certificateHolderExistsValidator } from '@features/certificate-holder/validators/certificate-holder-exists.validator'
import { certificateHolderEmailValidator } from '@features/certificate-holder/validators/certificate-holder-email.validator'

@Injectable({ providedIn: 'root' })
export class CertificateHolderFormService {
  private readonly fb = inject(FormBuilder)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly apiService = inject(CertificateHolderService)

  public buildForm(): FormGroup<CertificateHolderForm> {
    return this.fb.group({
      uuid: new FormControl<string | null>(null),
      companyName: new FormControl<string | null>('', { validators: [Validators.required] }),
      country: new FormControl<string | null>('', { validators: [Validators.required] }),
      freetext: new FormControl<string | null>(''),
      usedIn: new FormControl<CertificateHolderUsedIn[] | null>([]),
      recipientAddresses: this.fb.array<FormControl<CertificateHolderEmail>>([])
    }) as FormGroup<CertificateHolderForm>
  }

  public addAsyncValidators(
    form: FormGroup<CertificateHolderForm>,
    initialCompanyName?: string,
    initialCountry?: string,
    initialFreetext?: string
  ): void {
    form.setAsyncValidators([certificateHolderExistsValidator(this.apiService, this.globalErrorService, initialCompanyName, initialCountry, initialFreetext)])
    form.updateValueAndValidity({ emitEvent: false })
    form.markAllAsTouched()
  }

  public populateFormArray(form: FormGroup<CertificateHolderForm>, addresses: CertificateHolderEmail[]): void {
    const array = form.controls.recipientAddresses
    array.clear()

    if (addresses.length > 0) {
      addresses.forEach(address => {
        array.push(
          this.fb.control<CertificateHolderEmail>(address, {
            nonNullable: true,
            validators: [certificateHolderEmailValidator()]
          })
        )
      })
    }
  }

  public createBlankEmailControl(currentRecipientAddresses: CertificateHolderEmail[]): FormControl<CertificateHolderEmail> {
    return this.fb.control<CertificateHolderEmail>(
      { uuid: null, email: '' },
      { nonNullable: true, validators: [certificateHolderEmailValidator(currentRecipientAddresses)] }
    )
  }

}
